(() => {
  'use strict';
  // Authoritative event renderer shared across the existing Brand, Place and
  // Event templates. Own only event containers: business copy, merchandise,
  // photographs, forms and other threads' template changes remain untouched.
  const configNode = document.querySelector('[data-minty-event-view]');
  if (!configNode || window.mintyEventViewStarted) return;
  const C = window.MintyEventCore;
  if (!C || C.version !== '2026-10-12.2') return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  window.mintyEventViewStarted = true;
  const node = (tag, text, className) => {
    const n = document.createElement(tag);
    if (text !== undefined) n.textContent = text;
    if (className) n.className = className;
    return n;
  };
  const href = (kind, handle) => '/pages/' + kind + '/' + encodeURIComponent(handle);
  const link = (text, url) => {
    const a = node('a', text); a.href = url; return a;
  };
  const canonicalHosts = (event) => {
    const p = node('p', undefined, 'minty-event-hosts');
    if (!event.hosts.length) { p.textContent = 'Host not yet confirmed'; return p; }
    p.append(document.createTextNode('Hosted / performed by '));
    event.hosts.forEach((host, i) => {
      if (i) p.append(document.createTextNode(', '));
      p.append(link(host.name, href('brand', host.handle)));
    });
    return p;
  };
  const pattern = (a) => {
    const days = a.days || [];
    if (C.norm(a.recurrence) === 'weekly') return 'Weekly: ' + days.join(', ');
    if (C.norm(a.recurrence) === 'monthly') return 'Monthly: ' + (a.monthly_occurrence || 'ordinal unconfirmed') + ' ' + days.join(', ');
    return '';
  };
  function card(a, row, past) {
    const item = node('article', undefined, 'mt-place-card minty-event-card');
    item.dataset.eventHandle = a.handle;
    item.append(node('p', C.whenText(a, row), 'minty-event-when'));
    const heading = node('h3');
    heading.append(link(a.title || a.name || 'Event', href('events', a.handle)));
    item.append(heading);
    const repeat = pattern(a);
    if (repeat) item.append(node('p', repeat, 'minty-event-repeat'));
    if (a.place_handle) item.append(link(a.place_name || 'View venue', href('place', a.place_handle)));
    item.append(canonicalHosts(a));
    const address=[a.place_street_address,a.place_city,a.place_state,a.place_zip_code].filter(Boolean).join(', ');
    if(address)item.append(node('p',address,'minty-event-address'));
    const phone=String(a.place_phone_tel || '').trim();
    if(/^\+?[0-9]{7,15}$/.test(phone))item.append(link('Venue: '+(a.place_phone || phone),'tel:'+phone));
    if (a.description) {
      // Canonical event descriptions are plain text. Never execute scraped HTML.
      item.append(node('p', String(a.description).replace(/<[^>]*>/g, ''), 'minty-event-description'));
    }
    const actions = node('div', undefined, 'minty-event-actions');
    const calendar = past ? '' : C.calendarUrl(a);
    if (calendar) {
      const cal = link('Add to Google Calendar', calendar);
      cal.target = '_blank'; cal.rel = 'noopener noreferrer';
      actions.append(cal);
    }
    actions.append(link('Event details', href('events', a.handle)));
    if(address){const directions=link('Directions','https://www.google.com/maps/dir/?api=1&destination='+encodeURIComponent(address));directions.target='_blank';directions.rel='noopener noreferrer';actions.append(directions);}
    const share=node('button','Share');share.type='button';
    share.addEventListener('click',async()=>{const url=location.origin+href('events',a.handle);try{if(navigator.share)await navigator.share({title:a.title || 'Event',url});else if(navigator.clipboard){await navigator.clipboard.writeText(url);share.textContent='Link copied';}}catch{}});
    actions.append(share);
    if (!past && row?.dateOnly) actions.append(node('span', 'Calendar export is available when the showtime is confirmed.'));
    item.append(actions);
    return item;
  }
  const owned = [];
  function own(target, build) {
    if (!target) return;
    let currentChild;
    const apply = (force = false) => {
      if (!force && target.childNodes.length === 1 && target.firstChild === currentChild) return;
      currentChild = build();
      currentChild.dataset.mintyScheduleRenderer = C.version;
      target.replaceChildren(currentChild);
      target.dataset.mintyScheduleReady = 'true';
      target.removeAttribute('aria-busy');
      correctStructuredData();
    };
    // Older inline theme consumers can finish their fetch after this module.
    // Reconcile their output before painting, without rewriting unrelated files.
    new MutationObserver(() => apply()).observe(target, {childList: true});
    owned.push(() => apply(true));
    apply();
  }
  let currentEvents = [];
  let indexReady = false;
  function correctStructuredData() {
    if (!indexReady) return;
    for (const script of document.querySelectorAll('head script[type="application/ld+json"]')) {
      if (script.id === 'minty-accurate-event-jsonld') continue;
      try {
        const parsed = JSON.parse(script.textContent);
        const objects = Array.isArray(parsed) ? parsed : [parsed];
        if (objects.length && objects.every(x => x && x['@type'] === 'Event')) script.remove();
      } catch {}
    }
    let script = document.getElementById('minty-accurate-event-jsonld');
    if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.id = 'minty-accurate-event-jsonld'; document.head.append(script); }
    const values = currentEvents.flatMap(a => {
      const r = C.occurrence(a);
      if (!r || r.unknown || !r.day) return [];
      const event = {'@context':'https://schema.org','@type':'Event',name:a.title || a.name || 'Event',url:location.origin + href('events', a.handle),startDate:r.start ? r.start.toISOString() : r.day};
      if (r.end) event.endDate = r.end.toISOString();
      if (a.place_name) event.location = {'@type':'Place',name:a.place_name,url:location.origin + href('place', a.place_handle || ''),address:{'@type':'PostalAddress',streetAddress:a.place_street_address || '',addressLocality:a.place_city || '',addressRegion:a.place_state || '',postalCode:a.place_zip_code || '',addressCountry:'US'}};
      if (a.hosts.length) event.performer = a.hosts.map(h => ({'@type':'Organization',name:h.name,url:location.origin + href('brand', h.handle)}));
      return [event];
    });
    const text = JSON.stringify(values);
    if (script.textContent !== text) script.textContent = text;
  }
  const sortPast = (a,b) => String(C.pastOccurrence(b).day || '').localeCompare(String(C.pastOccurrence(a).day || ''));
  const selectedTypes=new Set();
  function eventFilters(){
    const types=[...new Set(currentEvents.flatMap(a=>a.event_types || []))].sort();
    if(types.length<2)return null;
    const controls=node('div',undefined,'minty-accurate-event-filters');controls.setAttribute('aria-label','Filter events by type');
    for(const type of ['',...types]){const b=node('button',type || 'All');b.type='button';b.dataset.scheduleType=type;b.setAttribute('aria-pressed',String(type?selectedTypes.has(type):!selectedTypes.size));b.addEventListener('click',()=>{if(!type)selectedTypes.clear();else if(selectedTypes.has(type))selectedTypes.delete(type);else selectedTypes.add(type);owned.forEach(refresh=>refresh());});controls.append(b);}
    return controls;
  }
  function list(past, title, id) {
    const out = node('div');
    const rows = past
      ? currentEvents.filter(a => C.isPast(a)).sort(sortPast).map(a => ({a, ...C.pastOccurrence(a)}))
      : C.select(currentEvents).rows;
    if (!rows.length) return out;
    const h = node('h2', title); h.id = id; out.append(h);
    if(!past || !C.select(currentEvents).rows.length){const filters=eventFilters();if(filters)out.append(filters);}
    const matching=rows.filter(row=>!selectedTypes.size || (row.a.event_types || []).some(t=>selectedTypes.has(t)));
    if(!matching.length)out.append(node('p','No events match the selected types.'));
    matching.forEach(row => out.append(card(row.a, row, past)));
    return out;
  }
  function correctDirect(event) {
    const when = document.getElementById('event-when');
    const cal = document.getElementById('event-gcal');
    const renderWhen = () => {
      const box = node('div');
      if (!event) box.textContent = 'The current schedule is not confirmed. Please check with the organizer.';
      else {
        const r = C.occurrence(event);
        box.append(node('p', C.whenText(event, r || C.pastOccurrence(event))));
        const repeat = pattern(event); if (repeat) box.append(node('p', repeat));
        if (!r) box.append(node('p', 'This performance or series has ended.'));
        box.append(canonicalHosts(event));
      }
      return box;
    };
    own(when, renderWhen);
    if (cal) {
      const fix = () => {
        const url = event ? C.calendarUrl(event) : '';
        if (url) {
          if (cal.getAttribute('href') !== url) cal.setAttribute('href', url);
          if (cal.style.display !== 'inline-flex') cal.style.display = 'inline-flex';
          cal.removeAttribute('aria-disabled');
        } else {
          if (cal.hasAttribute('href')) cal.removeAttribute('href');
          if (cal.style.display !== 'none') cal.style.display = 'none';
          if (cal.getAttribute('aria-disabled') !== 'true') cal.setAttribute('aria-disabled','true');
        }
        if (!cal.dataset.mintyScheduleReady) cal.dataset.mintyScheduleReady = 'true';
      };
      new MutationObserver(fix).observe(cal,{attributes:true,attributeFilter:['href','style']});
      owned.push(fix); fix();
    }
  }
  C.loadIndex(config.indexUrl).then(index => {
    const all = C.flatten(index);
    const kind = config.kind;
    if (kind === 'brand') {
      const b = Object.values(index).find(b => b?.handle === config.handle);
      const places = new Set((b?.places || []).map(p => p.handle));
      const direct = new Set((b?.appearances || []).map(a => a.handle));
      currentEvents = all.filter(a => places.has(a.place_handle) || direct.has(a.handle) || a.hosts.some(h => h.handle === config.handle));
      indexReady = true;
      own(document.getElementById('brand-appearances'), () => list(false,'Events','section-appearances'));
      own(document.getElementById('brand-appearances-past'), () => list(true,'Past Events','section-past-appearances'));
    } else if (kind === 'place') {
      currentEvents = all.filter(a => a.place_handle === config.handle);
      indexReady = true;
      own(document.getElementById('place-appearances'), () => list(false,'Happening Here','section-appearances'));
    } else if (kind === 'appearances') {
      const event = all.find(a => a.handle === config.handle);
      currentEvents = event ? [event] : []; indexReady = true;
      correctDirect(event);
    }
    const status=document.querySelector("[data-minty-schedule-status]");if(status)status.remove();
    window.mintyEventViewReady = true;
    setInterval(() => { if (!document.hidden) owned.forEach(refresh => refresh()); },60000);
  }).catch(() => {
    const status=document.querySelector("[data-minty-schedule-status]");if(status)status.remove();
    window.mintyEventViewFailed = true;
    const targets = config.kind === 'brand' ? ['brand-appearances','brand-appearances-past'] : config.kind === 'place' ? ['place-appearances'] : ['event-when'];
    targets.forEach((id,i) => own(document.getElementById(id),() => node('div',i ? '' : 'The schedule could not be loaded. Please check with the venue or organizer.')));
    const cal=document.getElementById('event-gcal');if(cal){cal.removeAttribute('href');cal.style.display='none';}
  });
})();
